/*
 * Which primary KPIs a CROSS-platform strip states when it is focused on one
 * platform.
 *
 * THE PROBLEM THIS EXISTS TO SOLVE
 *
 * Platform Analytics has one KPI strip and two jobs. Unfocused it describes a
 * mixed flight, where a click-oriented vocabulary — spend, impressions,
 * clicks, CTR — is the only vocabulary every channel shares. Focused on ONE
 * platform it describes that platform, and the shared vocabulary stops being
 * the right one: a CTV solo view spent two of its eight primary positions on
 * Clicks (a constant, zero-filled 0) and CTR (a permanent `—`), while the
 * questions CTV is actually bought against — how many plays started, how many
 * finished, what share finished — had nowhere to appear.
 *
 * The dedicated CTV page had already answered this correctly in its profile.
 * The focused strip could not read that answer, so it inherited the generic
 * one, and the same advertiser saw two different accounts of the same buy
 * depending on which page they were standing on.
 *
 * WHAT THIS MODULE IS
 *
 * The seam between those two surfaces, and nothing else. It holds no metric
 * list of its own: it resolves the platform's registry profile and returns
 * THAT profile's `commandMetricKeys`. The focused strip and the dedicated page
 * are therefore the same list read twice, which is a structural guarantee
 * rather than a convention two files are asked to honour. If the CTV profile
 * gains a metric, the focused strip gains it in the same commit.
 *
 * WHAT IT DELIBERATELY DOES NOT DO
 *
 * It does not decide anything for platforms that have not declared a KPI
 * profile kind. A null return means "the generic strip, unchanged" — so
 * Display, Search, Meta, TikTok and the all-platforms blend keep the exact
 * strip they had, with Clicks and CTR in their primary positions where those
 * are genuinely the metrics that matter. This module can only ever add an
 * explicit, declared exception.
 */

import {
  CLICK_OUTCOME_FIRST_KPI_PROFILE,
  PLATFORM_ANALYTICS_PROFILES,
  TRUEVIEW_DELIVERY_KPI_PROFILE,
  VIDEO_COMPLETION_FIRST_KPI_PROFILE,
  resolvePlatformMetric,
} from "./platformAnalyticsProfiles";

export {
  CLICK_OUTCOME_FIRST_KPI_PROFILE,
  TRUEVIEW_DELIVERY_KPI_PROFILE,
  VIDEO_COMPLETION_FIRST_KPI_PROFILE,
};

/*
 * Platform keys reach this module from three vocabularies — canonical channel
 * keys, branding keys, and the focus state a page holds — so they are folded
 * to the registry's spelling rather than trusted to arrive in it.
 */
const normalizePlatformKey = (value) =>
  String(value == null ? "" : value)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

/*
 * Aliases the canonical layer emits for a platform the registry keys
 * differently. Only spellings actually observed in canonical payloads are
 * listed: a speculative alias would claim coverage this table has not earned.
 */
const PLATFORM_KEY_ALIASES = Object.freeze({
  connected_tv: "ctv",
  connectedtv: "ctv",
});

const resolveProfileKey = (platformKey) => {
  const normalized = normalizePlatformKey(platformKey);
  return PLATFORM_KEY_ALIASES[normalized] || normalized;
};

/**
 * The KPI profile kind declared for a platform, or null.
 *
 * Null is the default and the safe answer: it means the platform has made no
 * claim to a special strip and must keep the generic one.
 */
export function resolveFocusedKpiProfileKind(platformKey) {
  const profile = PLATFORM_ANALYTICS_PROFILES[resolveProfileKey(platformKey)];
  return profile?.focusedKpiProfile || null;
}

/**
 * Is this platform read on completed viewing rather than clicks?
 *
 * The single predicate every focused surface asks, so no page has to test for
 * "ctv" itself. Kept separate from `platformReadsCostPerCompletedView` in the
 * CPCV module on purpose: that one answers which metric owns the OUTCOME slot
 * (and is true of YouTube too), this one answers whether the entire strip is
 * replaced by the completion-first contract. YouTube reports honest clicks
 * against real impressions and declares its separate TrueView contract.
 */
export function isVideoCompletionFirstPlatform(platformKey) {
  return resolveFocusedKpiProfileKind(platformKey) === VIDEO_COMPLETION_FIRST_KPI_PROFILE;
}

/**
 * Is this platform read on clicks and what they produced?
 *
 * The counterpart predicate to the one above, and kept just as narrow: it is
 * true only for a platform that has DECLARED the click-and-outcome kind, never
 * inferred from "does not report video". Inference would sweep in every
 * channel that happens to carry no video counts this window — including video
 * inventory whose source was simply quiet — and replace its strip on the
 * strength of an absence.
 */
export function isClickOutcomeFirstPlatform(platformKey) {
  return resolveFocusedKpiProfileKind(platformKey) === CLICK_OUTCOME_FIRST_KPI_PROFILE;
}

/**
 * The ordered primary KPI metric keys for a focused platform, or null when the
 * generic strip applies.
 *
 * Returned as a copy so a caller cannot mutate the registry it came from.
 */
export function resolveFocusedKpiMetricKeys(platformKey) {
  const profile = PLATFORM_ANALYTICS_PROFILES[resolveProfileKey(platformKey)];
  if (!profile?.focusedKpiProfile) return null;
  const keys = Array.isArray(profile.commandMetricKeys) ? profile.commandMetricKeys : [];
  return keys.length ? [...keys] : null;
}

/**
 * The same list, resolved to the shared metric registry's entries.
 *
 * Labels, units and help copy come from ONE registry, so the focused strip
 * cannot describe `videoCompletions` as "Completed Views" on one page and
 * something else on the other. A key with no registry entry is dropped rather
 * than rendered as a blank card.
 */
export function resolveFocusedKpiMetrics(platformKey) {
  const keys = resolveFocusedKpiMetricKeys(platformKey);
  if (!keys) return null;
  return keys.map((key) => resolvePlatformMetric(key)).filter(Boolean);
}
